import React, { useState } from "react";
import { Card, Tooltip } from "antd";
import { useTranslation } from 'react-i18next';

interface ProfileCardProps {
  name: string;
  role: string;
  avatar: React.ReactNode;
}

const moods = [
  { emoji: "😁", label: "I am feeling Happy!" },
  { emoji: "😎", label: "I am feeling Confident!" },
  { emoji: "😇", label: "I am feeling Calm!" },
  { emoji: "🤩", label: "I am feeling Appreciated!" },
  { emoji: "😧", label: "I am feeling Anxious!" }, 
  { emoji: "😫", label: "I am feeling Low!" },
];

export const ProfileCard: React.FC<ProfileCardProps> = ({
  name,
  role,
  avatar,
}) => {
  const [selectedMood, setSelectedMood] = useState<string>("");
  const { t } = useTranslation();

  const floatingPositions: React.CSSProperties[] = [
    /* left of avatar */
    { top: "-36%", left: "-30%" },
    { top: "-6%", left: "-50%" },
    { top: "36%", left: "-28%" },
    { top: "66%", left: "-50%" },

    /* right of avatar */
    { top: "-42%", right: "-36%" },
    { top: "-12%", right: "-56%" },
    { top: "22%", left: "90%" },
    { top: "56%", left: "110%" },
  ];

  return (
    <>
      {/* ---------- extra CSS ---------- */}
      <style>
        {`
          @keyframes pulse {
            0%   { box-shadow:0 0 10px rgba(255,255,255,.5);   }
            50%  { box-shadow:0 0 20px rgba(255,255,255,.8);   }
            100% { box-shadow:0 0 10px rgba(255,255,255,.5);   }
          }

          /* bounce on hover */
          @keyframes bounce {
            0%,20%,50%,80%,100% { transform:translateY(0); }
            40% { transform:translateY(-8px); }
            60% { transform:translateY(-4px); }
          }
          .emoji-btn:hover { animation:bounce .6s; }
          .emoji-btn, .selected-emoji { cursor:pointer; }

          /* floating clones (smaller) */
          @keyframes float {
            0%,100% { transform:translateY(0); }
            50%     { transform:translateY(-8px); }
          }
          .floating-emoji{
            position:absolute;
            font-size:1.2rem;
            animation: float 4s ease-in-out infinite;
            pointer-events:none;
          }

          /* Mobile responsiveness */
          @media (max-width: 768px) {
            .floating-emoji {
              display: none !important;
            }
            .avatar-container {
              display: none !important;
            }
            .name-display {
              display: block !important;
            }
            .mood-section {
              display: none !important;
            }
          }

          @media (max-width: 480px) {
            .floating-emoji {
              font-size: 0.8rem;
            }
          }

          @media (min-width: 769px) {
            .name-display {
              display: none !important;
            }
          }
        `}
      </style>
      
      {/* ---------- card ---------- */}
      <Card
        className="relative h-full w-full rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
        style={{
          background:
            "linear-gradient(135deg, rgba(139, 92, 246, 0.8) 0%, rgba(109, 40, 217, 0.8) 100%)",
          border: "none",
          backdropFilter: "blur(10px)",
        }}
        bodyStyle={{
          height: "100px",
          padding: "0.5rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* --- welcome --- */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "0.1rem",
            fontSize: "clamp(1rem, 3vw, 1.2rem)",
            color: "white",
            marginLeft: "0.7rem",
            fontFamily: "Arial, sans-serif",
          }}
        >
          {t('welcome!')}
        </div>

        {/* --- name display for mobile --- */}
        <div
          className="name-display"
          style={{
            display: "none",
            color: "white",
            fontSize: "clamp(1.2rem, 4vw, 1.5rem)",
            fontWeight: 700,
            textAlign: "center",
            padding: "1rem 0.5rem",
            wordBreak: "break-word",
            fontFamily: "Arial, sans-serif",
          }}
        >
          {name}
        </div>

        {/* --- avatar --- */}
        <div
          className="avatar-container"
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "0.5rem",
          }}
        >
          <div style={{ position: "relative" }}>
            <div
              style={{
                width: "clamp(70px, 20vw, 90px)",
                height: "clamp(70px, 20vw, 90px)",
                borderRadius: "50%",
                background:
                  "linear-gradient(145deg, rgba(255,255,255,.3), rgba(255,255,255,.1))",
                padding: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 20px rgba(124, 58, 237, .4)",
                animation: "pulse 2s infinite",
              }}
            >
              <div
                style={{
                  borderRadius: "50%",
                  overflow: "hidden",
                  background: "white",
                  border: "2px solid rgba(255,255,255,.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {avatar}
              </div>
              {/* glow */}
              <div
                style={{
                  position: "absolute",
                  top: "-6px",
                  left: "-6px",
                  right: "-6px",
                  bottom: "-6px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, rgba(236,72,153,.6), rgba(139,92,246,.6))",
                  zIndex: -1,
                  filter: "blur(8px)",
                  transition: "opacity .3s",
                  opacity: .6,
                }}
              />
            </div>

            {/* floating emojis (show only when one is picked) */}
            {selectedMood &&
              floatingPositions.map((pos, i) => (
                <div key={i} className="floating-emoji" style={pos}>
                  {selectedMood}
                </div>
              ))}
          </div>
        </div>

        {/* --- name/role --- */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            textAlign: "center",
          }}
        >
          <div
            className="avatar-container"
            style={{
              color: "white",
              fontSize: "clamp(0.85rem, 2.5vw, 1rem)",
              fontWeight: 600,
              margin: "0 0 .4rem 0",
              lineHeight: 1.2,
              fontFamily: "Arial, sans-serif",
              wordBreak: "break-word",
              padding: "0 0.5rem",
            }}
          >
            {name}
          </div>

          {/* --- mood selector / result --- */}
          <div className="mood-section">
            {selectedMood ? (
              <>
                <div
                  style={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: "clamp(0.75rem, 2vw, 0.9rem)",
                    padding: "0 0.5rem",
                  }}
                >
                  I'm feeling&nbsp;
                  {
                    moods.find((m) => m.emoji === selectedMood)!.label.split(
                      " "
                    )[3]
                  }
                  !
                </div>

                {/* central big emoji (tap to reset) */}
                <div
                  className="cursor-pointer"
                  style={{
                    fontSize: "clamp(1.3rem, 4vw, 1.7rem)",
                    marginTop: ".3rem",
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectedMood("")}
                >
                  {selectedMood}
                </div>
              </>
            ) : (
              <>
                <div
                  style={{
                    color: "white",
                    fontSize: "clamp(0.75rem, 2vw, 0.9rem)",
                    fontWeight: 600,
                    marginBottom: ".2rem",
                    padding: "0 0.5rem",
                  }}
                >
                  {t('how_feeling')}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "0.15rem",
                    flexWrap: "nowrap",
                    padding: "0 0.5rem",
                  }}
                >
                  {moods.map(({ emoji, label }) => (
                    <Tooltip title={label} key={emoji}>
                      <div
                        className="emoji-btn cursor-pointer"
                        style={{
                          fontSize: "clamp(0.9rem, 2.5vw, 1.1rem)",
                          padding: ".1rem",
                          borderRadius: "50%",
                          touchAction: "manipulation",
                        }}
                        onClick={() => setSelectedMood(emoji)}
                      >
                        {emoji}
                      </div>
                    </Tooltip>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </Card>
    </>
  );
};

export default ProfileCard;
